import React, { useState, useEffect, createContext, useRef } from 'react'
import { useValue } from 'firebase-utils'

declare global {
    interface EmbeddedServiceBootstrapUtilAPI {
        launchChat?: () => void,
        showChatButton?: () => void,
        hideChatButton?: () => void
    }

    interface EmbeddedServiceBootstrap {
        settings: {
            language?: string,
            disableLaunchChatOnInit?: boolean,
            // whether the chat button should be hidden on initial load
            hideChatButtonOnLoad?: boolean,
            // allow additional unknown properties provided by upstream embed
            [key: string]: unknown
        }
        init: (
            orgId: string,
            deploymentName: string,
            targetURL: string,
            config: Record<string, unknown>
        ) => void
        utilAPI?: EmbeddedServiceBootstrapUtilAPI
    }

    interface Window {
        embeddedservice_bootstrap?: EmbeddedServiceBootstrap
        initEmbeddedMessaging?: () => void
        initChat?: (settings: Record<string, unknown>) => void
    }
}

interface Props {
    children: React.ReactChild
}

/**
 * Context for accessing the helper functions provided by this context.
 */
function noop () : void { /* no-op */ }
const defaultSalesForceContext = { chatReady: false, openChat: noop }
/** Default SalesForce context */
export const SalesForceContext = createContext(defaultSalesForceContext as { chatReady: boolean; openChat: () => void })

/**
 * Loads and initialises the salesforce client library, and providers helper functions
 * to work with salesforce.
 */
const SalesForceProvider = (props: Props): JSX.Element => {
    const chatSettings = useValue('sales_force_chat_settings')
    const [chatReady, setChatReady] = useState(false)
    const [pendingOpenRequest, setPendingOpenRequest] = useState(false)
    const chatSettingsRef = useRef(chatSettings)
    const scriptInitialisedRef = useRef(false)
    const legacyChatLoadedRef = useRef(false)
    const appendedScriptsRef = useRef<HTMLScriptElement[]>([])
    const loadLegacyChatRef = useRef<() => void>()
    const fallbackPendingRef = useRef(false)
    const pendingOpenRequestRef = useRef(false)

    chatSettingsRef.current = chatSettings

    useEffect(() => {
        if (scriptInitialisedRef.current) return
        scriptInitialisedRef.current = true

        const appendedScripts = appendedScriptsRef.current

        const loadLegacyChat = () => {
            console.debug('SalesForceProvider: loadLegacyChat called')
            if (legacyChatLoadedRef.current) return

            let settings: Record<string, unknown> = {}
            const currentSettings = chatSettingsRef.current

            if (!currentSettings) {
                console.warn('Salesforce chat settings unavailable; legacy chat fallback skipped')
                fallbackPendingRef.current = true
                return
            }

            if (typeof currentSettings === 'string') {
                try {
                    settings = JSON.parse(currentSettings)
                } catch (error) {
                    console.error('Failed to parse Salesforce chat settings', error)
                    fallbackPendingRef.current = true
                    return
                }
            }

            fallbackPendingRef.current = false
            legacyChatLoadedRef.current = true

            const onLegacyLibraryLoaded = () => {
                console.debug('SalesForceProvider: legacy ESW library loaded, preparing snapIn script', settings?.communityURL)
                if (!settings?.communityURL) {
                    console.error('Salesforce legacy chat settings missing communityURL')
                    return
                }

                const snapInScript = document.createElement('script')
                snapInScript.setAttribute('data-testid', 'salesforce-snapin-script')
                snapInScript.src = `${settings.communityURL}/resource/phoneEinsteinBot/js/SnapInChat.js?${Date.now()}`
                snapInScript.onload = () => {
                    if (typeof window.initChat === 'function') {
                        console.debug('SalesForceProvider: SnapInChat loaded, calling window.initChat')
                        window.initChat(settings)
                        setChatReady(true)
                    } else {
                        console.error('window.initChat is not defined after loading SnapIn script')
                    }
                }
                snapInScript.onerror = (error) => {
                    console.error('Failed to load Salesforce SnapIn script', error)
                }

                document.body.appendChild(snapInScript)
                appendedScripts.push(snapInScript)
            }

            const legacyLibraryScript = document.createElement('script')
            legacyLibraryScript.setAttribute('data-testid', 'salesforce-legacy-esw-script')
            legacyLibraryScript.src = 'https://service.force.com/embeddedservice/5.0/esw.min.js'
            legacyLibraryScript.onload = onLegacyLibraryLoaded
            legacyLibraryScript.onerror = (error) => {
                console.error('Failed to load legacy Salesforce ESW library', error)
            }

            document.body.appendChild(legacyLibraryScript)
            appendedScripts.push(legacyLibraryScript)
        }

        loadLegacyChatRef.current = loadLegacyChat

        const initEmbeddedMessaging = () => {
            console.debug('SalesForceProvider: initEmbeddedMessaging called')
            try {
                if (!window.embeddedservice_bootstrap) {
                    throw new Error('embeddedservice_bootstrap not available')
                }

                window.embeddedservice_bootstrap.settings.language = 'en_US'
                window.embeddedservice_bootstrap.settings.disableLaunchChatOnInit = true // seems to do nothing
                window.embeddedservice_bootstrap.init(
                    '00D41000002iMoz',
                    'Phone_Service_My_Site_Deployment',
                    'https://phone.my.site.com/ESWPhoneServiceMySite1760459654910',
                    {
                        scrt2URL: 'https://phone.my.salesforce-scrt.com'
                    }
                )

                setChatReady(true)
                console.debug('SalesForceProvider: embeddedservice_bootstrap.init finished, chatReady set to true')
                if (pendingOpenRequestRef.current) {
                    pendingOpenRequestRef.current = false
                    setPendingOpenRequest(false)
                }
            } catch (err) {
                console.error('Error loading Embedded Messaging: ', err)
                loadLegacyChat() // Fallback to legacy chat
            }
        }

        window.addEventListener('onEmbeddedMessagingConversationClosed', () => {
            console.log('chat close ::::: ')
            // standard disable - if customer clicks the close icon we want the pop-up and icon hidden.
            try {
                if (window.embeddedservice_bootstrap) {
                    // Ensure settings object exists then set the flag
                    const settings = window.embeddedservice_bootstrap.settings ?? (window.embeddedservice_bootstrap.settings = {})
                    settings.hideChatButtonOnLoad = true
                }
            } catch (e) {
                console.error('SalesForceProvider: error while setting hideChatButtonOnLoad', e)
            }
        })
        window.initEmbeddedMessaging = initEmbeddedMessaging

        const script = document.createElement('script')
        script.setAttribute('data-testid', 'salesforce-bootstrap-script')
        script.src = 'https://phone.my.site.com/ESWPhoneServiceMySite1760459654910/assets/js/bootstrap.min.js'
        script.onload = () => {
            console.debug('SalesForceProvider: bootstrap script loaded, calling initEmbeddedMessaging')
            window.initEmbeddedMessaging && window.initEmbeddedMessaging()
        }
        script.onerror = (error) => {
            console.error('Failed to load Salesforce Embedded Messaging bootstrap script', error)
            loadLegacyChat()
        }
        script.async = true
        document.body.appendChild(script)
        appendedScripts.push(script)
        console.debug('SalesForceProvider: appended bootstrap script', script.src)

        return () => {
            appendedScripts.forEach((tag) => {
                if (document.body.contains(tag)) {
                    document.body.removeChild(tag)
                }
            })
            console.debug('SalesForceProvider: cleaning up appended scripts')
            appendedScripts.length = 0
            if (window.initEmbeddedMessaging === initEmbeddedMessaging) {
                delete window.initEmbeddedMessaging
            }
            scriptInitialisedRef.current = false
            legacyChatLoadedRef.current = false
            fallbackPendingRef.current = false
            loadLegacyChatRef.current = undefined
            pendingOpenRequestRef.current = false
        }
    }, [])

    useEffect(() => { // hide the chat button when chat is ready for new ESW
        // put in a 3 second timeout to let the button load because of some race condition... also this is not cleaned up, todo fix this
        setTimeout(() => {
            try {
                if (chatReady && window?.embeddedservice_bootstrap?.utilAPI?.hideChatButton) {
                    console.debug('SalesForceProvider: calling utilAPI.hideChatButton')
                    window.embeddedservice_bootstrap.utilAPI.hideChatButton() // hide the button after it loads
                }
            } catch (e) {
                console.error('SalesForceProvider: error while attempting to hide chat button', e)
            }
        }, 3000)
    }, [chatReady])

    useEffect(() => {
        if (!fallbackPendingRef.current) return
        loadLegacyChatRef.current?.()
    }, [chatSettings])

    useEffect(() => {
        pendingOpenRequestRef.current = pendingOpenRequest
    }, [pendingOpenRequest])

    const openChat = () => {
        console.debug('SalesForceProvider.openChat called', { hasUtilAPI: !!window.embeddedservice_bootstrap?.utilAPI })
        if (window.embeddedservice_bootstrap?.utilAPI?.launchChat) {
            console.debug('SalesForceProvider: calling utilAPI.launchChat()')
            window.embeddedservice_bootstrap.utilAPI.launchChat()
            pendingOpenRequestRef.current = false
            setPendingOpenRequest(false)
            return // return if the modern api is available else continue to legacy method
        }

        console.debug('SalesForceProvider: utilAPI.launchChat not available, falling back to DOM method')

        pendingOpenRequestRef.current = true
        setPendingOpenRequest(true)

        const sfChatElement = document.getElementsByClassName('embeddedServiceHelpButton')[0] as HTMLElement
        const sfChatOpenElement = document.querySelector('.embeddedServiceHelpButton .helpButton .helpButtonEnabled.uiButton') as HTMLElement
        console.debug('SalesForceProvider: attempting DOM fallback', { sfChatElement, sfChatOpenElement })
        if (!sfChatElement || !sfChatOpenElement) {
            console.warn('SalesForceProvider: DOM fallback elements not found')
            return
        }
        try {
            sfChatElement.style.display = 'block'
            sfChatOpenElement.click()
        } catch (e) {
            console.error('SalesForceProvider: error clicking legacy chat element', e)
        }
    }

    return (
        <SalesForceContext.Provider
            value={{
                chatReady,
                openChat
            }}
        >
            {props.children}
        </SalesForceContext.Provider>
    )
}

export default SalesForceProvider
